import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { User, Phone, Shield, Building, Key, Loader2, Calendar, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Profile = () => {
  const { user, offices, currentOffice, getUserRole, isSuperAdmin, isOfficeAdmin, isEmployee, changePin, pinResetRequired } = useAuth();
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChangePin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPin !== confirmPin) {
      setError('New PINs do not match');
      return;
    }

    if (newPin.length !== 4) {
      setError('PIN must be 4 digits');
      return;
    }

    setSubmitting(true);

    const result = await changePin(currentPin, newPin);

    if (result.success) {
      setSuccess('PIN changed successfully');
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      setTimeout(() => {
        setShowChangePinModal(false);
        setSuccess('');
      }, 2000);
    } else {
      setError(result.error);
    }

    setSubmitting(false);
  };

  const getRoleBadge = () => {
    if (isSuperAdmin()) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Shield className="h-3 w-3 mr-1" />
          Super Admin
        </Badge>
      );
    }
    if (isOfficeAdmin()) {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Building className="h-3 w-3 mr-1" />
          Office Admin
        </Badge>
      );
    }
    if (isEmployee()) {
      return (
        <Badge className="bg-green-100 text-green-800">
          <User className="h-3 w-3 mr-1" />
          {getUserRole()}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
        <p className="text-gray-600 dark:text-gray-400">
          View your profile information and manage your account
        </p>
      </div>

      {/* PIN Reset Warning */}
      {pinResetRequired && (
        <Alert variant="destructive" data-testid="pin-reset-warning">
          <AlertDescription className="flex items-center justify-between">
            <span>You are using a default PIN. Please change it for security.</span>
            <Button size="sm" variant="outline" onClick={() => setShowChangePinModal(true)}>
              Change PIN
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your personal and account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar & Name */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-2xl">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {user?.full_name}
                </h2>
                <div className="mt-1">{getRoleBadge()}</div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div className="space-y-1">
                <Label className="text-sm text-gray-500">Phone Number</Label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{user?.phone}</span>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-sm text-gray-500">User ID</Label>
                <span className="font-mono text-sm">{user?.id?.slice(0, 8)}...</span>
              </div>

              <div className="space-y-1">
                <Label className="text-sm text-gray-500">Account Status</Label>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium text-green-600">Active</span>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-sm text-gray-500">Member Since</Label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Change PIN Button */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowChangePinModal(true)}
                data-testid="change-pin-button"
              >
                <Key className="h-4 w-4 mr-2" />
                Change PIN
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Office & Access Card */}
        <Card>
          <CardHeader>
            <CardTitle>Access & Permissions</CardTitle>
            <CardDescription>Your office assignments and roles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSuperAdmin() ? (
              <div className="p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-purple-900 dark:text-purple-100">Global Access</span>
                </div>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  You have super admin privileges with access to all offices and system settings.
                </p>
              </div>
            ) : (
              <>
                {currentOffice && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        {currentOffice.name}
                      </span>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {currentOffice.location}
                    </p>
                  </div>
                )}

                {offices?.length > 1 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-500">All Assigned Offices</Label>
                    {offices.map(office => (
                      <div
                        key={office.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{office.name}</p>
                          <p className="text-sm text-gray-500">{office.location}</p>
                        </div>
                        {currentOffice?.id === office.id && (
                          <Badge variant="secondary">Current</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Role Details */}
            <div className="pt-4 border-t">
              <Label className="text-sm text-gray-500">Your Roles</Label>
              <div className="mt-2 space-y-2">
                {user?.roles?.map((role, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    {role.role === 'super_admin' && <Shield className="h-4 w-4 text-purple-500" />}
                    {role.role === 'office_admin' && <Building className="h-4 w-4 text-blue-500" />}
                    {role.role === 'employee' && <User className="h-4 w-4 text-green-500" />}
                    <span className="capitalize">{role.role.replace('_', ' ')}</span>
                    {role.job_title && (
                      <Badge variant="outline" className="ml-auto">{role.job_title}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Section */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your recent actions and login history</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="p-2 bg-green-100 rounded-full">
              <Clock className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium">Last Login</p>
              <p className="text-sm text-gray-500">
                {user?.last_login
                  ? new Date(user.last_login).toLocaleString()
                  : 'This is your first login'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change PIN Modal */}
      <Dialog open={showChangePinModal} onOpenChange={setShowChangePinModal}>
        <DialogContent className="sm:max-w-md" data-testid="change-pin-modal">
          <DialogHeader>
            <DialogTitle>Change PIN</DialogTitle>
            <DialogDescription>Enter your current PIN and choose a new one</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert>
                <AlertDescription className="text-green-600">{success}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="current-pin">Current PIN</Label>
              <Input
                id="current-pin"
                type="password"
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value)}
                placeholder="Enter current PIN"
                maxLength={4}
                required
                data-testid="current-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pin">New PIN</Label>
              <Input
                id="new-pin"
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="Enter new 4-digit PIN"
                maxLength={4}
                required
                data-testid="new-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pin">Confirm New PIN</Label>
              <Input
                id="confirm-pin"
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                placeholder="Confirm new PIN"
                maxLength={4}
                required
                data-testid="confirm-pin-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChangePinModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-change-pin-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change PIN'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
